/**
 * Layer Builder Utilities
 * Factory functions and utilities for creating Mapbox layers
 * Eliminates code duplication in layer creation patterns
 */

import type {
  ExpressionSpecification,
  Map as MapboxMap,
  RasterLayer,
  RasterSourceSpecification,
  SymbolLayer,
} from 'mapbox-gl';

/**
 * Remove a layer and its source from the map
 * @param map - Mapbox map instance
 * @param layerId - ID of the layer to remove (can be partial match)
 * @param sourceId - ID of the source to remove
 */
export function removeLayerAndSource(map: MapboxMap, layerId: string | string[], sourceId?: string): void {
  if (!map || !map.getStyle) return;

  // Check if style is loaded before accessing it
  if (!map.isStyleLoaded()) return;

  const style = map.getStyle();
  if (!style || !style.layers) return;

  const layerIds = Array.isArray(layerId) ? layerId : [layerId];

  layerIds.forEach((id) => {
    const layers = style.layers.filter((layer) => layer.id === id || layer.id.includes(id));
    if (layers.length > 0) {
      layers.forEach((layer) => {
        if (map.getLayer(layer.id)) {
          map.removeLayer(layer.id);
        }
      });
    }
  });

  if (sourceId && style.sources) {
    const source = style.sources[sourceId];
    if (source && map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
  }
}

/**
 * Configuration for creating a point layer from CSV data
 */
export interface PointLayerConfig {
  sourceId: string;
  layerId: string;
  data: Array<Record<string, unknown>>;
  coordinateFields: {
    longitude: string;
    latitude: string;
  };
  nameField?: string;
  propertiesMapper?: (item: Record<string, unknown>, index: number) => Record<string, unknown>;
}

/**
 * Create GeoJSON FeatureCollection from point data
 * @param config - Configuration for point layer
 * @returns GeoJSON FeatureCollection
 */
export function createPointLayerData(config: PointLayerConfig): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: config.data.map((item, index) => ({
      type: 'Feature',
      properties: config.propertiesMapper ? config.propertiesMapper(item, index) : { ...item, id: index },
      geometry: {
        type: 'Point',
        coordinates: [
          parseFloat(String(item[config.coordinateFields.longitude])),
          parseFloat(String(item[config.coordinateFields.latitude])),
        ],
      },
    })),
  };
}

/**
 * Configuration for symbol layer
 */
export interface SymbolLayerConfig {
  layerId: string;
  sourceId: string;
  iconImage?: string;
  iconSize?: number;
  iconAllowOverlap?: boolean;
  iconIgnorePlacement?: boolean;
  textField?: string;
  textVariableAnchor?: SymbolLayer['layout']['text-variable-anchor'];
  textRadialOffset?: number;
  textJustify?: SymbolLayer['layout']['text-justify'];
  minzoom?: number;
  visibility?: 'visible' | 'none';
  paint?: SymbolLayer['paint'];
}

/**
 * Create a standard symbol layer configuration
 * @param config - Symbol layer configuration
 * @returns Mapbox layer configuration object
 */
export function createSymbolLayer(config: SymbolLayerConfig): SymbolLayer {
  const layer: SymbolLayer = {
    id: config.layerId,
    type: 'symbol',
    source: config.sourceId,
    layout: {
      visibility: config.visibility || 'visible',
    },
  };

  if (config.iconImage) {
    layer.layout['icon-image'] = config.iconImage;
  }

  if (config.iconSize) {
    layer.layout['icon-size'] = config.iconSize;
  }

  if (config.iconAllowOverlap !== undefined) {
    layer.layout['icon-allow-overlap'] = config.iconAllowOverlap;
  }

  if (config.iconIgnorePlacement !== undefined) {
    layer.layout['icon-ignore-placement'] = config.iconIgnorePlacement;
  }

  if (config.textField) {
    layer.layout['text-field'] = config.textField;
  }

  if (config.textVariableAnchor) {
    layer.layout['text-variable-anchor'] = config.textVariableAnchor;
  }

  if (config.textRadialOffset !== undefined) {
    layer.layout['text-radial-offset'] = config.textRadialOffset;
  }

  if (config.textJustify) {
    layer.layout['text-justify'] = config.textJustify;
  }

  if (config.minzoom !== undefined) {
    layer.minzoom = config.minzoom;
  }

  if (config.paint) {
    layer.paint = config.paint;
  }

  return layer;
}

/**
 * Configuration for raster layer
 */
export interface RasterLayerConfig {
  layerId: string;
  sourceId: string;
  tiles: string[];
  tileSize?: number;
  minzoom?: number;
  maxzoom?: number;
  visibility?: 'visible' | 'none';
}

/**
 * Create a raster layer configuration
 * @param config - Raster layer configuration
 * @returns Mapbox layer configuration object
 */
export function createRasterLayer(config: RasterLayerConfig): RasterLayer {
  return {
    id: config.layerId,
    type: 'raster',
    source: config.sourceId,
    minzoom: config.minzoom || 0,
    maxzoom: config.maxzoom || 22,
    layout: {
      visibility: config.visibility || 'none',
    },
  };
}

/**
 * Create a raster source configuration
 * @param config - Raster layer configuration
 * @returns Mapbox source configuration object
 */
export function createRasterSource(config: RasterLayerConfig): RasterSourceSpecification {
  return {
    type: 'raster',
    tiles: config.tiles,
    tileSize: config.tileSize || 256,
  };
}

/**
 * Standard icon size interpolation for zoom levels
 * @param minZoom - Minimum zoom level
 * @param maxZoom - Maximum zoom level
 * @param minSize - Minimum icon size
 * @param maxSize - Maximum icon size
 * @returns Icon size interpolation array
 */
export function createIconSizeInterpolation(
  minZoom: number = 5,
  maxZoom: number = 11,
  minSize: number = 0.25,
  maxSize: number = 0.6,
): ExpressionSpecification {
  return ['interpolate', ['linear'], ['zoom'], minZoom, minSize, maxZoom, maxSize];
}

/**
 * Standard text field with zoom-based visibility
 * @param fieldName - Property name to display
 * @param showAtZoom - Zoom level to start showing text
 * @returns Text field configuration
 */
export function createTextFieldWithZoom(fieldName: string, showAtZoom: number = 12): ExpressionSpecification {
  return ['step', ['zoom'], '', showAtZoom, ['get', fieldName]];
}
